import { EVENT } from "@/constants/event";
import { safeParse } from "@/utils/safeParse";
import classNames from "classnames";
import Link from "next/link";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Image from "../Image";
import { useLogin } from "../LoginModal";
import styles from "./index.module.less";
import RightNav, { RightNavPopover } from "./rightNav";
import Search from "./search";

import { LOCAL_STORAGE_KEY } from "@/constants/localStorageKey";
import useAppStore from "@/context/store";
import { getFollowFeeds } from "@/services";
import useRequest from "@/utils/useReq";
import { useMemoizedFn } from "ahooks";
import { last, omit } from "lodash";
import { Else, If, Then, When } from "react-if";
import GloablBanner from "../GlobalBanner";
import Avatar from "./Avatar";
import CreateEntry from "./CreateEntry";

export default function Header({ isMobile, searchWord, ads }) {
  const router = useRouter();
  const { headerHeight, setStore } = useAppStore(
    ({ headerHeight, setStore }) => ({
      headerHeight,
      setStore,
    })
  );

  const bannerAd = Array.isArray(ads)
    ? ads.find((ad) => ad?.position?.id === 51)
    : null;

  const rightNavAd = Array.isArray(ads)
    ? ads.find((ad) => ad?.position?.id === 58)
    : null;

  const [bannerVisible, setBannerVisible] = useState(!!bannerAd);
  // 首页控制搜索框的显示隐藏
  const [searchVisible, setSearchVisible] = useState(false);

  const headerRef = useRef(null);
  const isHome = router.pathname === "/";

  const onCloseBanner = useCallback(() => {
    setBannerVisible(false);
    setStore({
      headerHeight: 60,
    });
  }, [setStore]);

  const { user, unreadNum, dmUnread } = useAppStore(
    ({ user, unreadNum, dmUnread }) => ({
      user,
      unreadNum,
      dmUnread,
    })
  );

  useEffect(() => {
    const onHanlder = (event) => {
      setSearchVisible(event.detail);
    };
    window.addEventListener(EVENT.CHANGE_SEARCH_INPUT_VISIBLE, onHanlder);

    return () => {
      window.removeEventListener(EVENT.CHANGE_SEARCH_INPUT_VISIBLE, onHanlder);
    };
  }, []);

  return (
    <>
      <div
        className={classNames([styles.header, isHome ? styles.home : null])}
        data-content-source="导航栏"
        ref={headerRef}
      >
        <When condition={!isHome}>
          <GloablBanner
            onClose={onCloseBanner}
            visible={bannerVisible}
            record={bannerAd}
          />
        </When>
        <div className={styles.wrap}>
          <div className={classNames(styles.container)}>
            <Link href={user ? "/follow" : "/?keep=true"}>
              <a
                onClick={(event) => {
                  if (user) {
                    onClickAction(event, router);
                  }
                }}
                data-dispatch-event={EVENT.FOLLOW_PIN_LIST_RESET}
                data-gd-click="button_click"
                data-button-name="LOGO"
                rel="nofollow"
              >
                <Logo isHome={isHome} />
              </a>
            </Link>
            <LeftNav user={user} isHome={isHome} />
            <If condition={isMobile}>
              <Then>
                <div className={styles.searchWrapper} />
              </Then>
              <Else>
                <Search
                  className={classNames(styles.searchWrapper, {
                    [styles.hide]: isHome && !searchVisible,
                  })}
                  initValue={searchWord}
                />
              </Else>
            </If>
            <RightNavPopover isLogin={!!user} ad={rightNavAd} />
            {!isHome && <CreateEntry />}
            <RightNav user={user} unreadNum={unreadNum} dmUnread={dmUnread} />
            {user ? <Avatar /> : <Login />}
          </div>
        </div>
      </div>
      <When condition={!isHome}>
        {/* 
          - 如果是通过滚动使得广告消失，则高度不变，依然为140
          - 如果是通过按钮点击关闭广告则，高度为70
        */}
        <div
          className={styles.placeholder}
          style={{
            // height: `${spaceHeight || headerHeight}px`,
            height: `${headerHeight}px`,
            transition: ".2s ",
          }}
        />
      </When>
    </>
  );
}

function onClickAction(e, router) {
  if (e.ctrlKey || e.metaKey) {
    return;
  }
  e.preventDefault();
  const targetHref = e.currentTarget.getAttribute("href");
  if (router.pathname === targetHref) {
    const dispatchEvent = e.currentTarget.getAttribute("data-dispatch-event");
    window.dispatchEvent(new Event(dispatchEvent));
  } else {
    router.push(targetHref);
  }
}

export function Logo({ size = 24, width, height, hasText = false }) {
  const src = hasText
    ? "https://st0.dancf.com/static/02/202112300712-b428.svg"
    : "https://st0.dancf.com/static/02/202306090204-51f4.png";

  return (
    <Image
      className={styles.logo}
      src={src}
      alt="花瓣网"
      width={width || size}
      height={height || size}
      loading={"eager"}
    />
  );
}

function LeftNav({ user, isHome }) {
  const config = useAppStore((s) => s.config);
  /**
   * 导航配置 https://grocery.huaban.com/put/25
   * @type {{
   * key: string,
   * label: string,
   * href: string,
   * ref?: string
   * target?: string,
   * tag?: string,
   * style?: React.CSSProperties,
   * }[]}
   */
  const navLinks = useMemo(() => {
    const navs = config?.headNav || [];
    if (navs.length) {
      last(navs).style = {
        paddingRight: 10,
      };
    }

    return navs;
  }, [config]);

  const [navTagsShow, setNavTagsShow] = useState(false);

  // 隐藏不需要显示tag的navLinks
  const hideNavTag = useMemoizedFn((key) => {
    // 不需要显示tag的navLinks的key
    const keys = navLinks.filter((s) => !s.tag).map((s) => s.key);
    // 获取localStorage的值
    let navTagsShow;
    navTagsShow = safeParse(
      localStorage.getItem(LOCAL_STORAGE_KEY.HB_HEADER_NAV_TAG) || "{}",
      () => {
        return {};
      }
    );

    // omit是为了删除不需要显示tag的值
    if (key) {
      setNavTagsShow({
        ...omit(navTagsShow, keys),
        [key]: true,
      });
    } else {
      setNavTagsShow({
        ...omit(navTagsShow, keys),
      });
    }
  });

  useEffect(() => {
    hideNavTag(); //删除不需要显示tag的localStorage记录的值，避免下次配置不显示tag
  }, [hideNavTag]);

  useEffect(() => {
    // 同步localStorage
    localStorage.setItem(
      LOCAL_STORAGE_KEY.HB_HEADER_NAV_TAG,
      JSON.stringify(navTagsShow)
    );
  }, [navTagsShow]);

  const router = useRouter();
  const isDescovery =
    router.pathname.includes("/discovery") &&
    !router.pathname.includes("/discovery/[categoryId]/materials");

  const followActive = useMemo(() => {
    return /^\/follow(\/[[\]\w]+\/[[\]\w]+)?$/g.test(router.pathname);
  }, [router]);

  const { data: dottedActive } = useRequest(
    async () => {
      const since = window.localStorage.getItem("hb-follow-dotted-history-all");

      if (!since || followActive) {
        return false;
      }

      if (!user) {
        return false;
      }

      if (dottedActive) {
        return true;
      }

      const result = await getFollowFeeds({
        limit: 1,
        since,
      });

      return result?.feeds?.length > 0;
    },
    {
      refreshDeps: [followActive],
      pollingInterval: 60000,
      pollingWhenHidden: false,
    }
  );

  return (
    <div className={styles.nav}>
      {user && (
        <Link href="/follow">
          <a
            className={classNames({
              [styles.active]: followActive,
              [styles.dotted]: dottedActive,
            })}
            onClick={(event) => {
              onClickAction(event, router);
            }}
            data-dispatch-event={EVENT.FOLLOW_PIN_LIST_RESET}
            data-gd-click="button_click"
            data-button-name="关注"
          >
            关注
          </a>
        </Link>
      )}
      <Link href="/discovery">
        <a
          className={isDescovery ? styles.active : ""}
          onClick={(event) => {
            onClickAction(event, router);
          }}
          data-dispatch-event={EVENT.DISCOVERY_PIN_LIST_RESET}
          data-gd-click="button_click"
          data-button-name="发现"
        >
          发现
        </a>
      </Link>
      {!isHome && <span className={styles.divider}></span>}

      {navLinks.map((item) => (
        <NavLink
          key={item.key}
          className={classNames({
            [styles.active]: router.pathname.includes(item.href),
          })}
          {...item}
          tagShow={item.tag && navTagsShow && !navTagsShow[item.key]}
          onClick={() => {
            hideNavTag(item.key);
          }}
        />
      ))}
    </div>
  );
}

function Login() {
  const { login } = useLogin();
  return (
    <div
      className={styles.login}
      data-gd-click="button_click"
      data-button-name="登陆注册"
      onClick={async () => {
        login();
      }}
    >
      登录/注册
    </div>
  );
}

/**
 *
 * @param {{
 *  label: string,
 * href: string,
 * className: string,
 * tag: string,
 * tagShow: boolean,
 * } & React.HTMLAttributes<HTMLAnchorElement>}
 * @returns
 */
function NavLink({ label, href, className, tag, tagShow, ...rest }) {
  return (
    <Link href={href}>
      <a
        className={classNames({
          [className]: !!className,
          [styles.redTag]: tagShow,
        })}
        data-gd-click="button_click"
        data-button-name={label}
        data-red-tag={tag}
        {...rest}
      >
        {label}
      </a>
    </Link>
  );
}
